// Apoyos de presentación y validación de cliente para la CLABE (validación de
// cuenta con DRUO). Se SUMAN a los validadores de data/validators/clabe.validators.ts
// (formato y banco soportado), que siguen igual.
import {AbstractControl, ValidationErrors, ValidatorFn} from "@angular/forms";

export const LONGITUD_CLABE = 18

// Estructura oficial de la CLABE: banco (3) · plaza (3) · cuenta (11) · control (1).
const GRUPOS_CLABE = [3, 3, 11]

// Pesos del dígito de control (Banxico): 3, 7, 1 repetidos sobre los primeros 17.
const PESOS_CONTROL = [3, 7, 1]

export function soloDigitos(valor: string | null | undefined): string {
  return String(valor ?? '').replace(/\D+/g, '')
}

// «012180012345678901» → «012 180 01234567890 1». Lo que pase de 18 se queda en
// el último grupo (así se ve que sobra).
export function formatearClabe(valor: string | null | undefined): string {
  const digitos = soloDigitos(valor)
  const partes: string[] = []
  let i = 0
  for (const largo of GRUPOS_CLABE) {
    if (i >= digitos.length) break
    partes.push(digitos.slice(i, i + largo))
    i += largo
  }
  if (i < digitos.length) partes.push(digitos.slice(i))
  return partes.join(' ')
}

// Posición del cursor en el texto con formato justo después del dígito n.º `digitos`.
export function posicionTrasDigitos(texto: string, digitos: number): number {
  if (digitos <= 0) return 0
  let vistos = 0
  for (let i = 0; i < texto.length; i++) {
    if (texto[i] >= '0' && texto[i] <= '9') {
      vistos++
      if (vistos === digitos) return i + 1
    }
  }
  return texto.length
}

export function digitoControlClabe(primeros17: string): number {
  let suma = 0
  for (let i = 0; i < 17; i++) {
    suma += (Number(primeros17[i]) * PESOS_CONTROL[i % 3]) % 10
  }
  return (10 - (suma % 10)) % 10
}

// Una CLABE completa lleva 18 dígitos (antes pasaba con 3 o más).
export function clabeLongitudValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const digitos = soloDigitos(control.value)
    if (!digitos || digitos.length >= LONGITUD_CLABE) return null
    return {clabeLongitud: {faltan: LONGITUD_CLABE - digitos.length}}
  }
}

// El último dígito confirma los otros 17: atrapa un dígito mal copiado antes de
// mandarla a DRUO.
export function clabeDigitoControlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const valor = String(control.value ?? '')
    if (!/^\d{18}$/.test(valor)) return null
    return digitoControlClabe(valor) === Number(valor[17]) ? null : {clabeDigitoControl: true}
  }
}

// Nombres de los bancos de BANKS («BBVA BANCOMER», «BANCO DEL BAJIO») en un formato
// fácil de leer. Si ya viene con minúsculas (p. ej. bankName del API) se respeta.
const PALABRAS_BANCO: Record<string, string> = {
  BBVA: 'BBVA',
  HSBC: 'HSBC',
  '(BX+)': '(BX+)',
  'S.A.': 'S.A.',
  CIBANCO: 'CIBanco',
  'BANCOPPEL,': 'BanCoppel,',
  MEXICO: 'México',
  'MÉXICO': 'México',
  '(MEXICO)': '(México)',
  BAJIO: 'Bajío',
  MAS: 'Más',
  DE: 'de',
  DEL: 'del',
  POR: 'por',
  OF: 'of',
  Y: 'y'
}

const capitalizar = (palabra: string): string => palabra
  .toLowerCase()
  .split('-')
  .map(parte => parte.charAt(0).toUpperCase() + parte.slice(1))
  .join('-')

export function nombreBancoLegible(nombre: string | null | undefined): string {
  const texto = String(nombre ?? '').trim()
  if (!texto || texto !== texto.toUpperCase()) return texto
  return texto
    .split(/\s+/)
    .map((palabra, i) => {
      const fija = PALABRAS_BANCO[palabra]
      if (fija) return i === 0 ? fija.charAt(0).toUpperCase() + fija.slice(1) : fija
      return capitalizar(palabra)
    })
    .join(' ')
}

// Para buscar sin acentos ni mayúsculas.
export function normalizarBusqueda(texto: string | null | undefined): string {
  return String(texto ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
}
